import { useCallback, useState } from 'react';

import {
  FORM_STATUS,
  useFormStatus,
} from '../../contexts/form-status-provider';
import type { WixFile } from '../form/types';

import { type SignatureProps } from './signature-field-props';
import {
  SignatureFormat,
  SignatureMode,
  SignatureView,
} from './signature-types';
import { useSignatureValue } from './use-signature-value';
import { type DataURL } from './utils/data-url';

export const useSignatureField = ({
  target,
  value,
  onChange,
}: Pick<SignatureProps, 'target' | 'value' | 'onChange'>) => {
  const { handleChange, cancelUpload, signature, decodedValue } =
    useSignatureValue({
      value,
      onChange,
      target,
    });

  const [signatureMode, setSignatureMode] = useState<SignatureMode>(() => {
    if (signature == null) {
      return SignatureMode.DRAW;
    }
    switch (decodedValue?.mimeType) {
      case SignatureFormat.POINTS:
        return SignatureMode.DRAW;
      case SignatureFormat.TEXT:
        return SignatureMode.TYPE;
      default:
        return SignatureMode.UPLOAD;
    }
  });

  const { setFormStatus } = useFormStatus();

  const handleBeforeChange = useCallback(() => {
    cancelUpload();
    setFormStatus(target, FORM_STATUS.BUSY);
  }, [cancelUpload, setFormStatus, target]);

  const handleClear = useCallback(() => {
    cancelUpload();
    handleChange(null);
    setFormStatus(target, FORM_STATUS.READY);
  }, [cancelUpload, handleChange, setFormStatus, target]);

  const clearable = signature != null;

  const signatureValueForView = undefinedIfUnsupportedByCurrentMode(
    signatureMode,
    signature,
    decodedValue,
  );

  const signatureView: SignatureView = (() => {
    switch (signatureMode) {
      case SignatureMode.DRAW:
        return SignatureView.DRAW;
      case SignatureMode.TYPE:
        return SignatureView.TYPE;
      case SignatureMode.UPLOAD:
        return signatureValueForView != null
          ? SignatureView.IMAGE
          : SignatureView.UPLOAD;
      default:
        return SignatureView.DRAW;
    }
  })();

  return {
    signature,
    decodedValue,
    signatureMode,
    clearable,
    signatureValueForView,
    signatureView,

    handleChange,
    setSignatureMode,
    handleBeforeChange,
    handleClear,
  };
};

const ACCEPTED_FORMATS_BY_MODE = {
  [SignatureMode.DRAW]: (decodedValue?: DataURL) =>
    decodedValue?.mimeType === SignatureFormat.POINTS,
  [SignatureMode.TYPE]: (decodedValue?: DataURL) =>
    decodedValue?.mimeType === SignatureFormat.TEXT,
  [SignatureMode.UPLOAD]: (decodedValue?: DataURL) =>
    decodedValue?.mimeType !== SignatureFormat.POINTS &&
    decodedValue?.mimeType !== SignatureFormat.TEXT,
};

function undefinedIfUnsupportedByCurrentMode(
  mode: SignatureMode,
  signature?: WixFile,
  decodedValue?: DataURL,
): WixFile | undefined {
  if (!signature) {
    return undefined;
  }

  const isAcceptedFormat = ACCEPTED_FORMATS_BY_MODE[mode](decodedValue);
  return isAcceptedFormat ? signature : undefined;
}
